import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { usePatientWebIntentionsAccess } from '../../hooks/intentions/usePatientWebIntentionsAccess';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { VisitorDrawerState, visitorDrawerStateAtom } from '../state';

/**
 * Utility hook encapsulating shared logic for intentions drawer controls.
 *
 * @returns An object containing the necessary props for the intentions drawer control.
 * @property helperText - The text to display in the tooltip or menu item.
 * @property isDrawerOpen - Whether the intentions drawer is already open.
 * @property onClick - The function to call when the drawer control is clicked.
 * @property shouldShowButton - Whether the button should be shown.
 */
export function useIntentionsDrawerControls() {
  const t = useTranslate();
  const [drawerState, setDrawerState] = useAtom(visitorDrawerStateAtom);
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();

  const { hasAccess } = usePatientWebIntentionsAccess(patientShortId ?? '');
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const { userClicked } = useTrackEvent();

  const isDrawerOpen = drawerState === VisitorDrawerState.INTENTIONS;
  const helperText = isDrawerOpen
    ? t('telehealth-core:controls.closeIntentions')
    : t('telehealth-core:controls.openIntentions');

  const onClick = useCallback(() => {
    setDrawerState((prev) => {
      if (prev !== VisitorDrawerState.INTENTIONS) {
        userClicked(EventTypeEnum.INTENTION_MODULE_OPEN_BUTTON_CLICKED, {
          appointmentShortId: appointmentShortId ?? '',
        });
        return VisitorDrawerState.INTENTIONS;
      }
      return null;
    });
  }, [setDrawerState, userClicked, appointmentShortId]);

  return {
    helperText,
    isDrawerOpen,
    onClick,
    shouldShowButton: hasAccess && !!appointmentShortId,
  };
}
